import React from "react";

import { Field, FormRow, FormHelpers } from "../forms";
import { useFormInput } from "../../hooks/use-form-input";
import {
  RateChangePayload,
  useModifyPosition,
  useRateChangeValidator
} from "../../hooks/use-modify-position";
import { useAccessControl } from "../../app/use-access-control";
import { LoadingOverlay } from "../loading-overlay/loading-overlay";
import { useModal } from "../modal/modal";
import { StaffMember } from "../../hooks/use-user";

export interface UseRateChangeModalProps {
  staffMember: StaffMember;
}

export const useRateChangeModal = ({ staffMember }: UseRateChangeModalProps) => {
  const { activeCenterId } = useAccessControl();
  const { show, hide: hideModal, renderModal } = useModal();

  const { buffer, onChangeValue, resetBuffer } = useFormInput<RateChangePayload>({
    effectiveDate: "",
    hourlyRate: ""
  });

  const isValidRateChangePayload = useRateChangeValidator(buffer);
  const { rateChangeMutator } = useModifyPosition(activeCenterId, staffMember.id, hideModal);

  const onSave = () => {
    rateChangeMutator.mutate(buffer);
  };

  const showRateChangeModal = () => {
    resetBuffer();
    show();
  };

  const renderRateChangeModal = () =>
    renderModal({
      title: `RATE CHANGE — ${staffMember.displayName}`,
      body: (
        <LoadingOverlay loading={rateChangeMutator.isLoading}>
          <FormRow>
            <Field
              type="number"
              label="New hourly rate"
              required
              mode="edit"
              value={buffer.hourlyRate}
              step="0.01"
              min="0"
              onChangeValue={onChangeValue("hourlyRate")}
              onValidate={FormHelpers.isValidRequiredString}
            />
          </FormRow>

          <FormRow>
            <Field
              type="date"
              label="Effective Date"
              required
              mode="edit"
              value={buffer.effectiveDate}
              onChangeValue={onChangeValue("effectiveDate")}
              onValidate={FormHelpers.isValidRequiredDateString}
            />
          </FormRow>
        </LoadingOverlay>
      ),
      footer: (
        <>
          <button className="btn btn-secondary" type="button" onClick={hideModal}>
            Cancel
          </button>
          <button className="btn btn-primary" type="button" onClick={onSave} disabled={!isValidRateChangePayload}>
            Save
          </button>
        </>
      )
    });

  return {
    hideRateChangeModal: hideModal,
    showRateChangeModal,
    renderRateChangeModal
  };
};
